// Every reducer case that is about the factory.
//
// Split out of `commands.ts` for the reason its own PLAN §12.6 gives: that
// file was two thousand lines and thirty cases with nine planned phases queued
// to add more, and a file every feature edits is a file every feature
// conflicts in. Belts, mining, the machines, forging and the structures live
// here; the chess and the run live in their own modules.
//
// Types come from `commands.ts` with `import type` — erased at build, so this
// does not close a runtime import cycle — and the shared machinery comes from
// `../reduce`.

// The shared machinery every reducer case is built out of.
//
// Split out of `commands.ts`, which had reached two thousand lines and thirty
// cases with **nine planned phases queued to add more**. The plan's own §12.6
// names it the contention point and recommended exactly this split; a file
// every feature has to edit is a file every feature conflicts in.
//
// Arranged so that nothing here imports a *value* from `commands.ts`. The
// case modules take their types from there with `import type`, which is
// erased at build time, and their helpers from here — that is the whole of
// why there is no runtime import cycle, and it is worth keeping. A cycle
// between a reducer and its helpers works right up until module evaluation
// order changes, and then fails as an undefined function at startup.

// The command log is the save format, the undo mechanism, the replay
// mechanism, the telemetry substrate and (from Phase 4) the network protocol.
// Every state change goes through `applyCommand` — nothing may mutate
// GameState directly.




import { canTakeVariant, payVariant } from "./upgrades";
import {
  type Color,
  DIRECTIONS,
  KING,
  pieceColor,
  pieceType,
} from "../board";
import {
  bestBeltRoute,
  beltPathDirections,
  canPlaceBelt,
  placeBelt,
  removeBelt,
  repointBelt
} from "./belts";
import { BELT_BILL, payForBelt } from "./beltParts";
import { hasPower, targetingArrays } from "../campaign/powers";
import { gunOf, isTurretKind, shotsFor } from "./turret";
import {
  canLoadBelt,
  canMine,
  forgeOnce,
  forgeSquares,
  isFoundryAt,
  noteKingTurn,
  capacityAt,
  loadBelt,
  mineOnce
} from "./machines";
import { canForgeAt, coalitionFor, fundableSquares } from "./coalition";
import { detonate, isNuclearAt } from "../blast";
import {
  canBuild,
  buildStructure,
  demolishStructure,
  isBuildableKind,
  isSealedKind,
  reservedSquares,
  touches
} from "./structures";
import {
  beltOnAt,
  beltOwnerAt,
  type Direction,
  hasBelt,
  makeUnit,
  setBeltOn,
  kingTaskAt,
  dropOnGround,
  setHandoff,
  setDump,
  setKingTask,
  structureAt,
  withGrade,
  setKingTempo,
  TASK_FORGE,
  TASK_REFINE,
  setMintFacing,
  mintFacingOf,
  setTarget,
  setReserve,
  isArtifactKind,
} from "./state";
import {
  reframePlating,
  pieceAt,
  setVariant,
} from "../position";
import {
  MAX_GRADE,
  MINTABLE,
  PIECE_PLATING,
  structureRow,
  pieceVariant,
  VARIANT_PLAIN,
} from "../rules";
import { addTally, tallySide } from "../tally";
import type { Command, GameState} from "../commands";
import {
  IllegalCommandError,
  cloneState,
  requireOwnTurn,
  requireBuilder,
  requireKingSafe,
} from "../reduce";
import { forgeRefusal, settleAction, spendMove } from "../clock";
import {
  alightError,
  alightTrain,
  boardError,
  boardTrain,
  driveError,
  orderTrain
} from "./trains";
/**
 * Pay for and put down one part on a cloned state (§31 S2). The caller has
 * asked `canPlaceBelt`. The position's mirror follows at the end of the
 * command (`syncBeltMirror`).
 */
function layPart(next: GameState, sq: number, owner: Color, dir: Direction): void {
  payForBelt(next.position, next.industry, sq, owner);
  placeBelt(next.industry, sq, owner, dir);
}

export function reduceIndustry(state: GameState, command: Command): GameState | null {
  switch (command.type) {
    case "lay-belt": {
      const owner = requireBuilder(state, command.owner);
      const next = cloneState(state);
      const error = canPlaceBelt(next.position, next.industry, command.square, owner);
      if (error) throw new IllegalCommandError(`cannot lay belt: ${error}`);
      // Built at once, paid at once (§31 S2): a part is a structure.
      layPart(next, command.square, owner, command.direction);
      return next;
    }

    case "lay-belt-line": {
      const owner = requireBuilder(state, command.owner);
      // **A run may be any shape, so long as it feeds through itself.** Ends
      // off each other's rank and file turn a corner rather than being
      // refused; the only thing two endpoints cannot describe is a run of one
      // part, which has no direction to derive and wants `lay-belt`.
      const best = bestBeltRoute(
        state.position,
        state.industry,
        command.from,
        command.to,
        owner
      );
      if (!best) {
        throw new IllegalCommandError("a run of one part needs a direction of its own");
      }

      const next = cloneState(state);
      let laid = 0;
      for (let i = 0; i < best.squares.length; i++) {
        const square = best.squares[i];
        // A part of yours already there is turned to follow the run rather
        // than stopping it — see `canExtendBelt`.
        if (repointBelt(next.industry, square, owner, best.directions[i])) {
          laid++;
          continue;
        }
        // Lay as far as the ground allows and stop at the first square that
        // refuses. Placing the legal prefix beats rejecting the whole gesture:
        // a run that clips a heap halfway is still mostly what was wanted.
        if (canPlaceBelt(next.position, next.industry, square, owner)) break;
        layPart(next, square, owner, best.directions[i]);
        laid++;
      }
      if (laid === 0) {
        const why = canPlaceBelt(next.position, next.industry, best.squares[0], owner);
        throw new IllegalCommandError(`cannot lay belt: ${why}`);
      }
      return next;
    }

    case "mine": {
      const owner = requireBuilder(state, command.owner);
      const why = canMine(state.position, state.industry, command.square, owner);
      if (why) throw new IllegalCommandError(`cannot mine: ${why}`);
      const next = cloneState(state);
      // **Tallied, like the automatic pass.** Mining is deliberately available
      // two ways — the tick does it, and the chip on a selected piece does it —
      // and the button was the half nobody counted. Ore dug by hand vanished
      // from the run's own scorecard, and from any `collect` objective reading
      // it, which made an objective about picking ore up completable only by
      // *not touching the control the game offers for picking ore up*.
      const dug = mineOnce(next.position, next.industry, command.square);
      if (dug > 0 && owner === tallySide(next.run)) {
        next.tally = addTally(next.tally, { mined: dug });
      }
      return next;
    }

    case "load-belt": {
      const owner = requireBuilder(state, command.owner);
      const why = canLoadBelt(state.position, state.industry, command.square, owner);
      if (why) throw new IllegalCommandError(`cannot load: ${why}`);
      const next = cloneState(state);
      // The same turn's worth the automatic pass moves. Pressing the button is
      // the moment you get to *watch* it happen, so the two have to agree — a
      // click that moved one unit where the tick moves two would make loading
      // by hand strictly worse than forgetting to.
      loadBelt(next.position, next.industry, command.square, owner);
      return next;
    }

    case "lay-belt-path": {
      const owner = requireBuilder(state, command.owner);
      const dirs = beltPathDirections(command.squares);
      if (!dirs) throw new IllegalCommandError("a path must step between neighbours");

      const next = cloneState(state);
      let laid = 0;
      for (let i = 0; i < command.squares.length; i++) {
        const square = command.squares[i];
        // Drawing over your own track re-aims it to follow the new run. That
        // is what makes "draw the lane again, the way you meant it" the fix
        // for a run pointing at a machine that has since moved.
        if (repointBelt(next.industry, square, owner, dirs[i])) {
          laid++;
          continue;
        }
        // Lay what the ground allows and skip the rest, rather than refusing
        // the whole gesture: a drawn path that clips one blocked square is
        // still mostly what was wanted.
        if (canPlaceBelt(next.position, next.industry, square, owner)) continue;
        layPart(next, square, owner, dirs[i]);
        laid++;
      }
      if (laid === 0) throw new IllegalCommandError("cannot lay any of that path");
      return next;
    }

    case "rotate-belt": {
      const owner = requireBuilder(state, command.owner);
      const next = cloneState(state);
      // Re-pointing an existing part is free: it costs neither material nor
      // tempo, so it may be done as often as you like — on your own turn,
      // which is `requireBuilder`'s gate (§29 U1). Kept free by §31 Q5.
      if (repointBelt(next.industry, command.square, owner, command.direction)) return next;
      if (hasBelt(state.industry, command.square)) {
        throw new IllegalCommandError("not your belt");
      }
      throw new IllegalCommandError("no belt there");
    }

    case "remove-belt": {
      const owner = requireBuilder(state, command.owner);
      const next = cloneState(state);
      if (beltOwnerAt(state.industry, command.square) !== owner) {
        throw new IllegalCommandError("not your belt");
      }
      // Whatever was riding it goes on the ground, intact, where any piece
      // ending its move there picks it up — the same route a dropped crown
      // takes. Turning it into raw scrap made the remove tool a way to destroy
      // your own refining by tidying up.
      for (const unit of removeBelt(next.industry, command.square)) {
        dropOnGround(next.industry, command.square, unit);
      }
      // **And the part's own cobble comes back with it** (§31 S2), on the
      // ground beside the cargo. Lifting your own track is tidying up, not
      // destruction; a wreck is the other reading (`wreckBelt`).
      for (let i = 0; i < BELT_BILL.count; i++) {
        dropOnGround(next.industry, command.square, makeUnit(BELT_BILL.grade, 0, BELT_BILL.line));
      }
      // Demolition is free and unlimited; only construction costs material.
      return next;
    }

    case "toggle-belt": {
      const owner = requireBuilder(state, command.owner);
      if (beltOwnerAt(state.industry, command.square) !== owner) {
        throw new IllegalCommandError("not your belt");
      }
      const next = cloneState(state);
      setBeltOn(next.industry, command.square, beltOnAt(next.industry, command.square) ? 0 : 1);
      return next;
    }

    case "upgrade": {
      const owner = requireOwnTurn(state);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner) {
        throw new IllegalCommandError("cannot upgrade: not your piece");
      }
      // **A variant only** — a sidegrade, not a rung. The rank ladder was the
      // Clone Army's and went with it (§31 R2).
      const variant = command.variant;
      if (variant === VARIANT_PLAIN) {
        throw new IllegalCommandError("cannot upgrade: name a variant");
      }
      // **The turbo pair is the player's alone**, and a rule rather than an
      // accident of the planner being blind to variants. `tallySide` is the
      // core's existing "which army is the human's"; White outside a run.
      if (pieceVariant(variant).playerOnly && owner !== tallySide(state.run)) {
        throw new IllegalCommandError("cannot upgrade: not your army's to build");
      }
      const why = canTakeVariant(state.position, state.industry, command.square, owner, variant);
      if (why) throw new IllegalCommandError(`cannot upgrade: ${why}`);

      const next = cloneState(state);
      // Paid by one source — a forger beside it or an Armory in reach — with
      // its fuel (§31 P3, `upgrades.ts`).
      payVariant(next.position, next.industry, command.square, owner, variant);
      // **The piece is not replaced.** That is the whole difference between a
      // sidegrade and a rung: the rank, the facing, the buffers, the oven and
      // the posting are all untouched, and the only thing that changes is
      // which row it points at. So there is no `setPiece` here and no facing
      // to clear — a Marshal is a pawn and still moves the way it faces.
      setVariant(next.position, command.square, variant);
      // **A row's armor replaces its rank's** (§31 K1: a Turbo-Rook has 3
      // "instead of 10"), so plate and cap move by the difference; whatever
      // else was bolted on stays. Absent keeps the rank's.
      const plate = pieceVariant(variant).plating;
      if (plate !== undefined) {
        const rank = pieceType(pieceAt(next.position, command.square));
        reframePlating(next.position, command.square, plate - PIECE_PLATING[rank]);
      }

      // It changes what stands on the square, so it can open a line onto
      // your own king — refused exactly as a forge is.
      requireKingSafe(next.position, owner, "upgrade");

      // **Costs no move** (§31 T4): taking an upgrade is factory work.
      settleAction(next, owner);
      return next;
    }

    case "forge": {
      const owner = requireOwnTurn(state);
      // **Factory Flow** (§31 T4): forge only on a turn you have not moved.
      const flow = forgeRefusal(state, owner);
      if (flow) throw new IllegalCommandError(flow);
      // **A forge names a foundry or a square, and either is enough.** The
      // shelf clicks a machine and the hotbar clicks the ground; naming
      // neither is not a shorthand for anything, so it is refused rather than
      // guessed at.
      if (command.square === undefined && command.at === undefined) {
        throw new IllegalCommandError("a forge names a foundry or a square");
      }

      // Where it lands. Told, or the first square this foundry could lead a
      // paid-for forge onto — and where none can be paid for, its first free
      // square anyway, so the refusal below is about the money rather than
      // about there being nowhere to stand.
      const at =
        command.at ??
        (fundableSquares(
          state.position,
          state.industry,
          command.square!,
          owner,
          command.target
        )[0] ??
          forgeSquares(state.position, command.square!)[0]);
      if (at === undefined) throw new IllegalCommandError("cannot forge: no-room");
      if (
        command.square !== undefined &&
        !forgeSquares(state.position, command.square).includes(at)
      ) {
        throw new IllegalCommandError("that square is not beside the foundry");
      }

      const why = canForgeAt(
        state.position,
        state.industry,
        at,
        owner,
        command.target,
        command.square
      );
      if (why) throw new IllegalCommandError(`cannot forge: ${why}`);

      // **The lead: whoever the player named, else the lowest square paying.**
      // It decides which way a minted pawn looks and whose standing facing
      // applies, and the coalition is already sorted, so a forge aimed at a
      // square alone still lands the same pawn facing the same way on a replay.
      const lead =
        command.square ??
        coalitionFor(state.position, state.industry, at, owner, command.target)?.[0];
      if (lead === undefined) throw new IllegalCommandError("cannot forge: no-foundry");

      const next = cloneState(state);
      forgeOnce(
        next.position,
        next.industry,
        lead,
        owner,
        command.target,
        at,
        // **The foundry's own standing order, and it was never read.**
        //
        // `set-target` has always stored a facing and the shelf has always
        // drawn a dial for it — hashed, migrated with the piece, read back
        // into the control — and nothing consulted it when the pawn was
        // actually put down. `command.facing` is the only thing `forgeOnce`
        // ever saw, and no caller sets it: not the shelf, which dispatches
        // `forge` with a square and a target, and not the planner. So the
        // dial was decorative, and picking the opposite of wherever the pawn
        // happened to land read as being exactly 180 degrees out. Reported
        // from play as new pawns from a king ignoring the setting.
        //
        // Precedence is command, then policy, then the machine's own
        // reading: a facing named on the command is somebody saying it about
        // *this* mint, which must outrank a standing order, and `undefined`
        // from both leaves `defaultMintFacing` to point it away from
        // whatever made it. The policy lives in state and is hashed, so a
        // replayed log lands the same pawn facing the same way.
        command.facing ?? mintFacingOf(state.industry, lead)
      );
      // **And it may not leave your own king attacked.**
      //
      // A forge spends the turn, so without this a side in check could forge
      // every turn and sit there forever — the same hole `re-aim` closes with
      // a blanket refusal. It is *not* a blanket refusal here, and that is the
      // point: a minted piece lands on a square beside the foundry, so it can
      // genuinely block the line a check is coming down, and that is a real
      // and often the best answer to one. The rule is the ordinary rule of
      // chess — you may not end your turn in check — rather than a ban on
      // building while threatened.
      requireKingSafe(next.position, owner, "forge");

      // **No move, and no more moves** (§31 T4): any number of forges a turn,
      // as many as can be paid for, and the turn's moves are closed by the
      // first. The ticks come at End Turn.
      next.position.turnForged++;
      settleAction(next, owner);

      // The forge's own report, alone: no tick ran, so nothing else happened.
      next.lastTick = {
        moved: 0,
        mined: 0,
        cooked: 0,
        minted: [{ square: lead, type: command.target, at }]
      };
      return next;
    }

    case "aim-turret": {
      // §31 S8: "can rotate with a move" — a move spent, re-aim's rules:
      // turn-bound, owner-bound, refused in check (it moves nothing).
      const owner = requireBuilder(state, command.owner);
      if (state.status !== "playing") throw new IllegalCommandError(`cannot aim a turret in ${state.status}`);
      const existing = structureAt(state.industry, command.square);
      if (!existing || !existing.turret || !isTurretKind(existing.kind)) {
        throw new IllegalCommandError("no turret there");
      }
      if (existing.owner !== owner) throw new IllegalCommandError("not your turret");
      if (gunOf(existing.kind).shape !== "cone") throw new IllegalCommandError("a Laser fires all round — nothing to aim");
      if (!DIRECTIONS.includes(command.facing)) throw new IllegalCommandError("no such facing");
      if (existing.turret.facing === command.facing) throw new IllegalCommandError("it already faces that way");

      const next = cloneState(state);
      const turret = structureAt(next.industry, command.square)!;
      turret.turret = { ...turret.turret!, facing: command.facing };
      next.position.halfmove++;
      spendMove(state, next, owner, false);
      return next;
    }

    case "build": {
      const owner = requireOwnTurn(state);
      // **Turrets are behind the Targeting Array's `emplacement` power**
      // (§31 A7: Mainframe ∧ Turret Manager Badge).
      //
      // Refused *here*, in the rules, and not only at the server: a command
      // that conjures a capability is stopped where the rules are, which is
      // the line `next-stage` already holds. §14.10 hazard 24 names the
      // opposite mistake by name — gate it in the reducer, not only in the
      // UI, or the socket is the gate. A fallen array stops new turret
      // builds; existing turrets keep firing, being structures themselves.
      // No count cap (§26 M2's retired by A7).
      if (isTurretKind(command.kind)) {
        if (!hasPower(state.industry, owner, "emplacement")) {
          throw new IllegalCommandError("turrets need a Targeting Array");
        }
        if (command.facing !== undefined && !DIRECTIONS.includes(command.facing)) {
          throw new IllegalCommandError("no such facing");
        }
      } else if (!isBuildableKind(command.kind)) {
        // The artifact structures are placed from tokens (`place-structure`),
        // never built by a foundry — the token and `artifactBill` (§31 A3–A5).
        // Stores, walls, the Armory and the power network are (§31 S4–S6).
        throw new IllegalCommandError("that is placed from a token, not built");
      }
      const why = canBuild(
        state.position,
        state.industry,
        command.square,
        owner,
        command.kind,
        command.at,
        // A level's outstanding objectives keep their squares walkable: a
        // building takes its square off the board, so walling one is a level
        // that can never be finished.
        reservedSquares(state)
      );
      if (why) throw new IllegalCommandError(`cannot build: ${why}`);

      const next = cloneState(state);
      buildStructure(
        next.position,
        next.industry,
        command.square,
        owner,
        command.kind,
        command.at
      );
      // A turret stands with this round's shots for the arrays powered now,
      // facing where it was told (§31 S8).
      const gun = structureAt(next.industry, command.at)?.turret;
      if (gun) {
        gun.shots = shotsFor(command.kind, targetingArrays(next.industry, owner));
        if (command.facing !== undefined) gun.facing = command.facing;
      }
      // A king does one job a turn, and paying for a building is one — so it
      // books the turn and moves the tempo streak exactly as forging does.
      // Through `noteKingTurn` rather than by writing the flag: the streak is
      // the half a raw `setKingActedAt` would silently skip.
      if (pieceType(pieceAt(next.position, command.square)) === KING) {
        noteKingTurn(next.industry, command.square, TASK_FORGE);
      }
      // It may not leave your own king attacked — see the forge. An
      // obstruction blocks lines (§31 S1), so this can resolve a check too.
      requireKingSafe(next.position, owner, "build");
      // **Costs no move** (§31 T4), and Factory Flow does not bind it.
      settleAction(next, owner);
      return next;
    }

    case "demolish": {
      const owner = requireOwnTurn(state);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner) {
        throw new IllegalCommandError("not your piece");
      }
      if (!structureAt(state.industry, command.at)) {
        throw new IllegalCommandError("nothing to take down there");
      }
      // Diagonals included: it is a physical act rather than a material link,
      // and a bishop touching a building corner-on is plainly standing beside
      // it. Material moves cardinally into a structure; hands reach further.
      if (!touches(command.square, command.at)) {
        throw new IllegalCommandError("you are not beside it");
      }
      // An occupied car of yours is not taken apart under its riders (§31 S7).
      const there = structureAt(state.industry, command.at)!;
      if (there.owner === owner && (there.train?.riders?.length ?? 0) > 0) {
        throw new IllegalCommandError("set its riders down first");
      }

      const next = cloneState(state);
      // An enemy's Nuclear Generator taken down goes off (§31 S6); the
      // owner's own dismantle is no destruction and does not.
      const blast =
        isNuclearAt(state.industry, command.at) &&
        structureAt(state.industry, command.at)!.owner !== owner;
      demolishStructure(next.position, next.industry, command.square, command.at);
      if (blast) detonate(next.position, next.industry, command.at);
      // Putting a square back on the board can *open* a line onto your own
      // king, which is the same rule read the other way round.
      requireKingSafe(next.position, owner, "demolish");
      // A piece's own act, so it is still one of the turn's moves.
      next.position.halfmove++;
      spendMove(state, next, owner, false);
      return next;
    }

    case "drive-train": {
      const owner = requireOwnTurn(state);
      const why = driveError(state.industry, owner, command.at, command.to);
      if (why) throw new IllegalCommandError(`cannot drive: ${why}`);
      const next = cloneState(state);
      orderTrain(next.industry, command.at, command.to);
      settleAction(next, owner);
      return next;
    }

    case "board-train": {
      const owner = requireOwnTurn(state);
      const why = boardError(state.position, state.industry, owner, command.car, command.from);
      if (why) throw new IllegalCommandError(`cannot board: ${why}`);
      const next = cloneState(state);
      boardTrain(next.position, next.industry, command.car, command.from);
      // Lifting a blocker off a line may not leave your own king attacked.
      requireKingSafe(next.position, owner, "board");
      settleAction(next, owner);
      return next;
    }

    case "alight-train": {
      const owner = requireOwnTurn(state);
      const rider = command.rider ?? 0;
      const why = alightError(state.position, state.industry, owner, command.car, command.to, rider);
      if (why) throw new IllegalCommandError(`cannot alight: ${why}`);
      const next = cloneState(state);
      alightTrain(next.position, next.industry, command.car, command.to, rider);
      requireKingSafe(next.position, owner, "alight");
      settleAction(next, owner);
      return next;
    }

    case "set-store-policy": {
      const owner = requireBuilder(state, command.owner ?? state.position.turn);
      const structure = structureAt(state.industry, command.square);
      if (!structure || structure.owner !== owner) {
        throw new IllegalCommandError("not your store");
      }
      // **An artifact structure has no taps** (§26 F6). It is a power's
      // address, not a warehouse — sealed at creation (`newStructure`), and
      // this is the door that must not unseal it: with an accept bit open the
      // routing field courts it with deliveries and a Banner quietly becomes
      // an eighty-unit warehouse nobody paid for. A1 revisits for the altar,
      // whose yield is carried off like a store's.
      if (isArtifactKind(structure.kind)) {
        throw new IllegalCommandError("no taps: it is a power's address, not a warehouse");
      }
      // Walls and the power network hold nothing to tap (§31 S4–S6); the
      // Armory and the Train Car are stockpiles and do (§33 I2).
      if (isSealedKind(structure.kind) || structureRow(structure.kind).burnsCoal) {
        throw new IllegalCommandError("no taps: only a stockpile has them");
      }
      if (command.grade < 0 || command.grade > MAX_GRADE) {
        throw new IllegalCommandError("no such grade");
      }
      const next = cloneState(state);
      const mine = structureAt(next.industry, command.square)!;
      mine.accept = withGrade(mine.accept, command.grade, command.accept);
      mine.offer = withGrade(mine.offer, command.grade, command.offer);
      return next;
    }

    case "set-handoff": {
      const owner = requireBuilder(state, command.owner);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner) {
        throw new IllegalCommandError("not your piece");
      }
      const next = cloneState(state);
      setHandoff(next.industry, command.square, command.facing === null ? 0 : command.facing + 1);
      return next;
    }

    case "set-reserve": {
      const owner = requireBuilder(state, command.owner);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner) {
        throw new IllegalCommandError("not your piece");
      }
      const next = cloneState(state);
      // Clamped to what the piece could actually hold: a reserve above the
      // buffer is a piece that never passes anything on, which is `hold` said
      // in a way that does not look like `hold`.
      const cap = capacityAt(state.position, state.industry, command.square);
      setReserve(next.industry, command.square, Math.max(0, Math.min(command.units, cap)));
      return next;
    }

    case "set-dump": {
      const owner = requireBuilder(state, command.owner);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner) {
        throw new IllegalCommandError("not your piece");
      }
      const next = cloneState(state);
      setDump(next.industry, command.square, command.dump);
      return next;
    }

    case "set-king-task": {
      const owner = requireBuilder(state, command.owner);
      const piece = pieceAt(state.position, command.square);
      if (!piece || pieceColor(piece) !== owner || pieceType(piece) !== KING) {
        throw new IllegalCommandError("not your king");
      }
      // `TASK_RETIRED` (0) is refused rather than accepted-and-ignored: it was
      // "mine", the king does not dig, and a posting that silently became
      // something else would be a control that lies about what it did.
      if (
        command.task !== null &&
        (command.task < TASK_REFINE || command.task > TASK_FORGE)
      ) {
        throw new IllegalCommandError("no such job");
      }
      const next = cloneState(state);
      setKingTask(next.industry, command.square, command.task ?? -1);
      // A new posting starts from a standing start: tempo is the reward for
      // leaving a king where it is, so switching has to cost it.
      if (kingTaskAt(state.industry, command.square) !== (command.task ?? -1)) {
        setKingTempo(next.industry, command.square, 1);
        next.industry.kingLastTask.delete(command.square);
      }
      return next;
    }

    case "set-target": {
      // Machine policy, not a move: owner-bound like `set-dump`,
      // `set-king-task` and `set-handoff`, and free of tempo — a recipe is not
      // an action. It waits for your turn all the same (§29 U1): a turn has to
      // hold one side's commands or undo has no boundary to take back.
      const owner = requireBuilder(state, command.owner ?? state.position.turn);
      const piece = pieceAt(state.position, command.square);
      // Every foundry, which is rooks *and* kings. It was `!== 4`, so a king
      // could forge but could not be told what to save for or which way to
      // point what it mints — the one piece whose minted pawns most need a
      // direction, since it puts them down all around itself.
      if (!piece || pieceColor(piece) !== owner || !isFoundryAt(state.position, state.industry, command.square)) {
        throw new IllegalCommandError("not your foundry");
      }
      if (command.target !== 0 && !MINTABLE.includes(command.target)) {
        throw new IllegalCommandError("not mintable");
      }
      const next = cloneState(state);
      setTarget(next.industry, command.square, command.target);
      // Stored +1 so 0 keeps meaning "unset — use the side's forward".
      if (command.facing !== undefined) {
        setMintFacing(next.industry, command.square, command.facing + 1);
      }
      return next;
    }

    default:
      // Not one of ours. `applyCommand` asks each reducer in turn, so "null"
      // means "somebody else's case" rather than "unknown command" — the
      // exhaustiveness check that catches a genuinely unhandled type stays in
      // `applyCommand`, where the whole union is in scope.
      return null;
  }
}
